import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import { setSection } from "../../../assignmentSlice";

import { typeInfo } from "../../../../../constants/typeInfo";
import {
  applyUnderline,
  normalizeEnglishAnswer,
} from "../../../../../utils/textUtils";

import CommonPage from "../../common/CommonPage";

// 한 문항에서 낱말 여러 개를 받아 적는 유형. 2026-09 모의고사 신유형 둘이 여기 온다.
//   vocabulary_definition  영영풀이 다섯 줄을 보고 글에서 낱말을 찾아 쓴다
//   vocabulary_initial     지문 빈칸의 첫 글자를 보고 낱말을 쓴다
// 칸이 하나뿐인 서술형 화면(DescriptiveConditionPage)으로 내보내면 아이가 다섯
// 낱말을 한 상자에 몰아 적게 되어 무엇이 몇 번 답인지 알 수 없다. 그래서 칸을
// 답 개수만큼 세로로 나눠 준다.

// "① gripping  ② lyrics …" / "revolutionary, concluded …" 둘 다 받는다.
const splitMultiAnswer = (answerText) => {
  const text = String(answerText ?? "").trim();
  if (!text) return [];
  if (/[①-⑮]/.test(text)) {
    return text
      .split(/[①-⑮]/)
      .map((one) => one.trim())
      .filter(Boolean);
  }
  return text
    .split(/[,\n]/)
    .map((one) => one.trim())
    .filter(Boolean);
};

// 칸 옆에 붙일 실마리. 영영풀이는 그 줄을, 첫 글자 유형은 지문의 `<b>f____</b>` 를 쓴다.
const hintListFor = (currentPage, answerCount) => {
  const conditionText = String(currentPage.text2 ?? "").trim();
  if (conditionText) {
    const lineList = conditionText
      .split("\n")
      .map((line) => line.replace(/^\s*[①-⑮]\s*/, "").trim())
      .filter(Boolean);
    if (lineList.length === answerCount) return lineList;
  }
  const boldList = [...String(currentPage.text ?? "").matchAll(/<b>(.*?)<\/b>/g)].map(
    (matched) => matched[1].trim()
  );
  if (boldList.length === answerCount) return boldList;
  return [];
};

const CIRCLED_LIST = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];

const MultiBlankPage = ({
  section,
  pageList,
  currentPage,
  currentIndex,
  setPageList,
  handlePreviousPage,
  handleNextPage,
}) => {
  const dispatch = useDispatch();

  const answerList = splitMultiAnswer(currentPage.answer);
  const hintList = hintListFor(currentPage, answerList.length);

  const [inputList, setInputList] = useState(answerList.map(() => ""));
  const [overlayVisible, setOverlayVisible] = useState(false);

  useEffect(() => {
    const saved = currentPage.isSubmitted ? currentPage.answer : currentPage.input;
    const savedList = splitMultiAnswer(saved);
    setInputList(
      answerList.map((_, index) => savedList[index] ?? "")
    );
  }, [currentIndex]);

  const handleChange = (index, value) => {
    setInputList((previous) =>
      previous.map((one, position) => (position === index ? value : one))
    );
  };

  const handleSubmit = () => {
    if (currentPage.isSubmitted) return;

    // 다섯 칸을 다 맞혀야 맞은 것으로 본다. 칸별 맞고 틀림은 아래에 표시해 준다.
    const correct = answerList.every(
      (one, index) =>
        normalizeEnglishAnswer(inputList[index] ?? "") === normalizeEnglishAnswer(one)
    );
    const newPageList = pageList.map((page, index) => {
      if (index === currentIndex) {
        return {
          ...page,
          input: inputList.join(", "),
          isSubmitted: true,
          isCorrect: correct,
        };
      }
      return page;
    });

    setOverlayVisible(true);
    setPageList(newPageList);
    dispatch(setSection({ ...section, answer: newPageList }));
    setTimeout(() => {
      setOverlayVisible(false);
      setInputList(answerList);
    }, 600);
  };

  const isMobileW = Math.min(window.innerWidth, window.screen.width) <= 768;

  return (
    <CommonPage
      currentIndex={currentIndex}
      handleNextPage={handleNextPage}
      handlePreviousPage={handlePreviousPage}
      totalNo={pageList.length}
      currentPage={currentPage}
      paragraph={currentPage.text || currentPage.paragraph}
      accentColor={typeInfo.paragraph.color}
      overlayVisible={overlayVisible}
      isCorrect={currentPage.isCorrect}
      handleSubmit={handleSubmit}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isMobileW ? "12px" : "18px",
          width: isMobileW
            ? `${Math.min(window.innerWidth, window.screen.width) - 56}px`
            : "100%",
          maxWidth: "680px",
          margin: "0 auto",
          boxSizing: "border-box",
          padding: isMobileW ? "0 4px" : 0,
        }}
      >
        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <span style={{ backgroundColor: "#0E141B", color: "#FFF", borderRadius: "8px", fontSize: isMobileW ? "12px" : "13px", fontWeight: 800, padding: "2px 10px" }}>
              Q{currentIndex + 1}
            </span>
            <span style={{ marginLeft: "auto", fontSize: isMobileW ? "12px" : "13px", color: "#7A8490", fontWeight: 700 }}>
              {currentIndex + 1} / {pageList.length}
            </span>
          </div>
          <div style={{ height: "4px", borderRadius: "999px", backgroundColor: "#EDF2F1", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${((currentIndex + 1) / Math.max(pageList.length, 1)) * 100}%`, backgroundColor: typeInfo.paragraph.color, borderRadius: "999px", transition: "width .25s" }} />
          </div>
        </div>

        <div
          style={{
            width: "100%",
            fontSize: isMobileW ? "16px" : "19px",
            fontWeight: 600,
            lineHeight: 1.75,
            textAlign: "left",
            wordBreak: "keep-all",
          }}
        >
          {applyUnderline(
            String(currentPage.question || "").replace(/^\s*\d+\.\s*/, "")
          )}
        </div>

        {/* 칸 하나에 낱말 하나. 실마리(영영풀이 줄, 첫 글자)가 그 칸 바로 위에 붙는다 */}
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: isMobileW ? "10px" : "12px",
            marginBottom: isMobileW ? "48px" : 0,
          }}
        >
          {answerList.map((one, index) => {
            const typed = inputList[index] ?? "";
            const matched =
              normalizeEnglishAnswer(typed) === normalizeEnglishAnswer(one);
            const showMark = currentPage.isSubmitted;
            return (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: isMobileW ? "8px" : "12px",
                  border: "1px solid #E6EDEC",
                  borderRadius: "12px",
                  backgroundColor: "#FFFFFF",
                  padding: isMobileW ? "9px 11px" : "10px 14px",
                }}
              >
                <span
                  style={{
                    color: typeInfo.paragraph.color,
                    fontWeight: 800,
                    fontSize: isMobileW ? "14px" : "16px",
                    flexShrink: 0,
                  }}
                >
                  {CIRCLED_LIST[index] || index + 1}
                </span>
                {hintList[index] && (
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: isMobileW ? "13px" : "15px",
                      lineHeight: 1.6,
                      color: "#3C4753",
                      wordBreak: "keep-all",
                    }}
                  >
                    {hintList[index]}
                  </span>
                )}
                <input
                  type="text"
                  value={typed}
                  onChange={(event) => handleChange(index, event.target.value)}
                  disabled={currentPage.isSubmitted}
                  style={{
                    width: hintList[index] ? (isMobileW ? "112px" : "170px") : "100%",
                    flexShrink: 0,
                    boxSizing: "border-box",
                    padding: isMobileW ? "7px 9px" : "8px 12px",
                    fontFamily: "inherit",
                    fontSize: isMobileW ? "15px" : "17px",
                    borderRadius: "9px",
                    border: `1px solid ${
                      showMark
                        ? matched
                          ? "#00A396"
                          : "#E0605F"
                        : typeInfo.paragraph.color
                    }`,
                    backgroundColor: currentPage.isSubmitted
                      ? typeInfo.paragraph.backgroundColor
                      : "#FFFFFF",
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </CommonPage>
  );
};

export default MultiBlankPage;
